/**
 * /r/<code> — pre-trusted referral landing (S-14, v1).
 * See docs/specs/2026-06-06-referral-s14-design.md §7.1.
 *
 * This is the v1 "pre-trusted-landing floor": true deep linking is deferred (§7.2,
 * blocked on a paid Apple Developer account). A referred friend who opens the
 * shared `https://washedafrica.com/r/<code>` link lands here knowing they were
 * invited, sees the code prominently, and is told to open the app and paste it at
 * signup — fulfilling frame requirement 2 (the friend lands pre-trusted) with zero
 * Apple-account dependency.
 *
 * v1 is UNPAID: there is NO cash / amount / credit copy anywhere on this page.
 *
 * Bilingual inline content follows the InclusPage/LegalPage convention (these
 * marketing routes keep copy inline rather than via @washed/i18n, whose catalog is
 * for the apps + operator console). Vouvoiement in FR.
 */
import { useState } from 'react';
import { Colophon } from '../sections/Colophon.js';
import type { WashedLocale } from '@washed/i18n';
import { extractReferralCodeFromUrl } from '../referral/referralCode.js';
import styles from './ReferralLandingPage.module.css';

export interface ReferralLandingPageProps {
  /** The extracted, uppercase-normalised code, or null when the URL had no valid code. */
  code: string | null;
  initialLocale?: WashedLocale;
}

interface ReferralContent {
  readonly homeLabel: string;
  readonly langLabel: string;
  readonly eyebrow: string;
  readonly title: string;
  readonly intro: string;
  readonly codeLabel: string;
  readonly instruction: string;
  readonly noCodeTitle: string;
  readonly noCodeBody: string;
  readonly storeLead: string;
  readonly appStoreLabel: string;
  readonly playStoreLabel: string;
  readonly waitlistNote: string;
  readonly waitlistCta: string;
}

const CONTENT: Record<WashedLocale, ReferralContent> = {
  fr: {
    homeLabel: 'Accueil',
    langLabel: 'English',
    eyebrow: 'Washed · une amie vous invite',
    title: 'Vous avez été invitée',
    intro:
      'Une amie pense que Washed est fait pour vous — des visites régulières de blanchisserie à domicile, à Lomé.',
    codeLabel: 'Votre code d’invitation',
    instruction: 'Ouvrez l’app et entrez ce code :',
    noCodeTitle: 'Lien d’invitation incomplet',
    noCodeBody:
      'Ce lien d’invitation ne contient pas de code valide. Demandez à votre amie de vous renvoyer son lien, ou rejoignez la liste ci-dessous.',
    storeLead: 'Téléchargez l’app Washed',
    appStoreLabel: 'App Store',
    playStoreLabel: 'Google Play',
    waitlistNote: 'Pas encore prête à télécharger ?',
    waitlistCta: 'Rejoindre la liste',
  },
  en: {
    homeLabel: 'Home',
    langLabel: 'Français',
    eyebrow: 'Washed · a friend invited you',
    title: 'You’ve been invited',
    intro: 'A friend thinks Washed is for you — recurring home laundry visits, in Lomé.',
    codeLabel: 'Your invitation code',
    instruction: 'Open the app and enter this code:',
    noCodeTitle: 'Incomplete invitation link',
    noCodeBody:
      'This invitation link doesn’t carry a valid code. Ask your friend to resend their link, or join the list below.',
    storeLead: 'Download the Washed app',
    appStoreLabel: 'App Store',
    playStoreLabel: 'Google Play',
    waitlistNote: 'Not ready to download yet?',
    waitlistCta: 'Join the list',
  },
};

export function ReferralLandingPage({ code, initialLocale = 'fr' }: ReferralLandingPageProps) {
  const [locale, setLocale] = useState<WashedLocale>(initialLocale);
  const c = CONTENT[locale];
  const toggle = () => setLocale(locale === 'fr' ? 'en' : 'fr');

  return (
    <div className={styles.page}>
      <div className={styles.navWrap}>
        <header className={styles.nav}>
          <a className={styles.brand} href="/" aria-label="Washed">
            washed<span className={styles.dot}>.</span>
          </a>
          <div className={styles.navLinks}>
            <a href="/">{c.homeLabel}</a>
            <button type="button" className={styles.langToggle} onClick={toggle}>
              {c.langLabel}
            </button>
          </div>
        </header>
      </div>

      <main className={styles.main}>
        <p className={`smallcaps ${styles.eyebrow}`}>{c.eyebrow}</p>
        <h1 className={styles.h1}>{c.title}</h1>
        <p className={styles.intro}>{c.intro}</p>

        {code === null ? (
          <section className={styles.noCodeCard} aria-label={c.noCodeTitle}>
            <h2 className={styles.noCodeTitle}>{c.noCodeTitle}</h2>
            <p className={styles.noCodeBody}>{c.noCodeBody}</p>
          </section>
        ) : (
          <section className={styles.codeCard} aria-label={c.codeLabel}>
            <p className={styles.codeLabel}>{c.codeLabel}</p>
            <p className={styles.code} data-testid="referral-code">
              {code}
            </p>
            <p className={styles.instruction}>{c.instruction}</p>
          </section>
        )}

        <div className={styles.stores}>
          <p className={styles.storeLead}>{c.storeLead}</p>
          <div className={styles.storeLinks}>
            {/* The app is not yet published to the stores (no paid Apple Developer
                account — launch blocker). Until store URLs exist, both badges route
                to the homepage waitlist so the page never dangles a dead link. */}
            <a className={styles.storeButton} href="/#rejoindre">
              {c.appStoreLabel}
            </a>
            <a className={styles.storeButton} href="/#rejoindre">
              {c.playStoreLabel}
            </a>
          </div>
          <p className={styles.waitlistNote}>{c.waitlistNote}</p>
          <a href="/#rejoindre" className={styles.waitlistCta}>
            {c.waitlistCta}
          </a>
        </div>
      </main>

      <Colophon locale={locale} />
    </div>
  );
}

/**
 * Route matcher for App.tsx. Returns the extracted code (string), `null` when the
 * path IS an `/r/...` route but carries no valid code (render the no-code state),
 * or `undefined` when the path is not a referral route at all (don't render this
 * page). The three-way result lets App distinguish "wrong page" from "referral page,
 * bad code".
 */
export function referralCodeFromPath(pathname: string): string | null | undefined {
  const path = pathname.replace(/\/+$/u, '') || '/';
  // Only claim the route when the first segment is `/r` (optionally with a code).
  if (path !== '/r' && !path.startsWith('/r/')) return undefined;
  return extractReferralCodeFromUrl(pathname);
}
